import { BulkVerificationRunView } from 'api/bitedb/entities/bulkVerificationRun.service';
import { checkErrorMayClearLater } from '../Components/verificationCheckErrors';

export const processedCount = (run: BulkVerificationRunView): number =>
  run.completed + run.failed + run.skipped;

export const remainingCount = (run: BulkVerificationRunView): number =>
  Math.max(0, run.total - processedCount(run));

/** The run reports at most this many failures, so only a run within it can be retried exactly. */
export const MAX_RETRYABLE_FAILURES = 100;

/** The failed Database Entities to run again, or none when the run's failures can't all be named. */
export const retryableFailedIds = (run: BulkVerificationRunView): string[] => {
  if (run.failed < 1 || run.failed > MAX_RETRYABLE_FAILURES) return [];
  return Array.from(
    new Set(run.failures.map((failure) => failure.databaseEntityId)),
  );
};

export const percentProcessed = (run: BulkVerificationRunView): number =>
  run.total > 0
    ? Math.min(100, Math.round((processedCount(run) / run.total) * 100))
    : 0;

/**
 * A failed or skipped entity's credit is given back. A completed entity whose
 * only finished checks were unbilled is refunded too, but the run view can't
 * tell those apart, so this can understate the credits returned.
 */
export const runCredits = (
  run: BulkVerificationRunView,
): { used: number; returned: number } => {
  const returned = Math.min(run.creditsReserved, run.failed + run.skipped);
  return { used: run.creditsReserved - returned, returned: returned };
};

interface TeamMember {
  id?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
}

/** "you", "Thandi M.", or "a teammate" when the starter isn't on the team list. */
export const startedByName = (
  requestedBy: string,
  currentUserId: string | undefined,
  members: TeamMember[] | undefined,
): string => {
  if (currentUserId && requestedBy === currentUserId) return 'you';
  const member = (members || []).find((m) => m?.id === requestedBy);
  if (!member) return 'a teammate';
  const first = (member.firstName || '').trim();
  const last = (member.lastName || '').trim();
  if (first) return last ? `${first} ${last[0]}.` : first;
  return member.email || 'a teammate';
};

export type FailureGroupKind = 'may-clear-later' | 'our-side';

export const FAILURE_GROUP_COPY: Record<
  FailureGroupKind,
  { title: string; body: string }
> = {
  'may-clear-later': {
    title: "The service didn't respond",
    body: 'Trying again later may help. Their credits were returned.',
  },
  'our-side': {
    title: 'A problem on our side',
    body: "Not a problem with these records. Their credits were returned; we've logged it.",
  },
};

/** The run's recorded failures by Check Error category, in #4406's two kinds. */
export const failureGroups = (
  run: BulkVerificationRunView,
): Array<{ kind: FailureGroupKind; databaseEntityIds: string[] }> => {
  const groups: Record<FailureGroupKind, string[]> = {
    'may-clear-later': [],
    'our-side': [],
  };
  run.failures.forEach((failure) => {
    const kind: FailureGroupKind = checkErrorMayClearLater(failure.reason)
      ? 'may-clear-later'
      : 'our-side';
    groups[kind].push(failure.databaseEntityId);
  });
  return (Object.keys(groups) as FailureGroupKind[])
    .filter((kind) => groups[kind].length > 0)
    .map((kind) => ({ kind: kind, databaseEntityIds: groups[kind] }));
};
